"use client"

import { memo } from "react"
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"

export interface PaginationProps {
  currentPage: number
  totalItems: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  dictionary?: any
}

function Pagination({ 
  currentPage, 
  totalItems, 
  itemsPerPage,
  onPageChange,
  dictionary
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage))
  const startIndex = (currentPage - 1) * itemsPerPage
  const currentPageSize = totalItems === 0 ? 0 :
    Math.min(itemsPerPage, totalItems - startIndex)

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pageNumbers: (number | string)[] = []
    if (totalPages <= 7) {
      // If 7 or fewer pages, show all
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i)
    } else {
      // Always show first page
      pageNumbers.push(1)
      if (currentPage > 3) pageNumbers.push('...')
      
      // Calculate start and end of current range
      let startRange = Math.max(2, currentPage - 1)
      let endRange = Math.min(totalPages - 1, currentPage + 1)
      
      // Adjust range if at edges
      if (currentPage <= 3) {
        endRange = 4
      } else if (currentPage >= totalPages - 2) {
        startRange = totalPages - 3
      }
      
      // Add range numbers
      for (let i = startRange; i <= endRange; i++) pageNumbers.push(i)
      
      // Add final ellipsis and last page if needed
      if (currentPage < totalPages - 2) pageNumbers.push('...')
      pageNumbers.push(totalPages)
    }
    return pageNumbers
  }
  
  return (
    <div className="flex items-center gap-4 text-sm text-muted-foreground">
      <div>
        {dictionary?.pagination?.page} {currentPage} {dictionary?.pagination?.of} {totalPages} ({currentPageSize} {dictionary?.pagination?.items})
      </div>
      <div className="flex items-center gap-1">
        <button 
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className="px-2 py-1 disabled:opacity-50"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>
        {getPageNumbers().map((pageNum, idx) => (
          pageNum === '...' ? (
            <span key={`ellipsis-${idx}`} className="px-2 py-1">...</span>
          ) : (
            <button
              key={`page-${pageNum}`}
              onClick={() => onPageChange(pageNum as number)}
              disabled={currentPage === pageNum}
              className={`px-2 py-1 min-w-[2rem] rounded ${
                currentPage === pageNum 
                  ? 'bg-muted text-foreground' 
                  : 'hover:bg-muted/50'
              }`}
            >
              {pageNum}
            </button>
          )
        ))}
        <button 
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className="px-2 py-1 disabled:opacity-50"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

Pagination.displayName = 'Pagination'

export default memo(Pagination)
